import { useMutation, useQueryClient } from '@tanstack/react-query';
import { withholdingsQueryKey } from './keys';
import { normalizeWithholding, sortWithholdings } from '../types';
import type {
  CreateWithholdingPayload,
  WithholdingChildEnvelope,
  WithholdingDto,
  WithholdingsHttpRequest,
} from '../types';

export interface UseAddManualWithholdingParams {
  documentId: string;
  /** Endpoint base de child (`/{id}/withholdings`). El POST va a esta ruta. */
  withholdingsBaseEndpoint: string;
  httpRequest: WithholdingsHttpRequest;
}

/**
 * Agrega una retención manual (`POST /{id}/withholdings`) → 201 `{child, header}`.
 *
 * `withholding_action` NO se envía: el backend lo calcula. La fila resultante
 * llega con `isManual = true`.
 *
 * - `onSuccess`: inserta la fila nueva en el cache.
 * - `onSettled`: invalida `['document-withholdings', documentId]` (totales cambian).
 */
export function useAddManualWithholding({
  documentId,
  withholdingsBaseEndpoint,
  httpRequest,
}: UseAddManualWithholdingParams) {
  const queryClient = useQueryClient();
  const key = withholdingsQueryKey(documentId);

  return useMutation<WithholdingDto, Error, CreateWithholdingPayload>({
    mutationFn: async (payload) => {
      const res = await httpRequest<WithholdingChildEnvelope>(
        'POST',
        withholdingsBaseEndpoint,
        payload,
      );
      return normalizeWithholding(res.child);
    },
    onSuccess: (created) => {
      queryClient.setQueryData<WithholdingDto[]>(key, (prev) =>
        sortWithholdings([...(prev ?? []).filter((row) => row.id !== created.id), created]),
      );
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: key });
    },
  });
}
